'use client';

import { useState, useMemo, useCallback } from 'react';
import { PageHeader } from '@/shared/layout';
import { Card, Badge, Button, Modal } from '@/shared/ui';
import {
  UtensilsCrossed, Calendar, Clock, Users,
  CheckCircle2, XCircle, AlertTriangle, Armchair,
  PenLine, BarChart3,
} from 'lucide-react';
import { cn } from '@/core/utils/cn';
import type { DiningReservation, Restaurant, SeatingPreference } from '../types/dining.types';
import { DiningReservationDrawer } from './DiningReservationDrawer';
import { useDiningData } from '../hooks/useDining';
import { useTranslation } from '@/core/i18n';
import { DINING_TAB_LABELS, RESERVATION_STATUS_KEYS } from './dining.constants';

/* -------------------------------------------------------------------------- */
/*  Constants                                                                 */
/* -------------------------------------------------------------------------- */

const STATUS_VARIANT: Record<string, 'green' | 'gold' | 'red' | 'outline'> = {
  Confirmed: 'green', Completed: 'outline', Cancelled: 'red',
};

type TabKey = 'all' | 'Confirmed' | 'Completed' | 'Cancelled';

const TABS = [
  { key: 'all', labelKey: DINING_TAB_LABELS.all },
  { key: 'Confirmed', labelKey: DINING_TAB_LABELS.Confirmed },
  { key: 'Completed', labelKey: DINING_TAB_LABELS.Completed },
  { key: 'Cancelled', labelKey: DINING_TAB_LABELS.Cancelled },
] as const;

/* -------------------------------------------------------------------------- */
/*  StatCard                                                                  */
/* -------------------------------------------------------------------------- */

function StatCard({
  id,
  label,
  value,
  icon: Icon,
  accent,
}: {
  id: string;
  label: string;
  value: number;
  icon: React.ElementType;
  accent: string;
}) {
  const { t } = useTranslation();
  return (
    <div id={id} className="rounded-xl border border-hms-border bg-hms-card p-4 flex items-center gap-3">
      <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', accent)}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-lg font-bold text-hms-text leading-none">{value}</p>
        <p className="text-[11px] text-hms-textDim mt-0.5">{label}</p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  GuestDiningReservations                                                   */
/* -------------------------------------------------------------------------- */

export function GuestDiningReservations() {
  const { t } = useTranslation();
  const { data: diningData } = useDiningData();
  const diningPayload = diningData?.data as unknown as { reservations?: DiningReservation[]; restaurants?: Restaurant[] };
  const apiReservations: DiningReservation[] = useMemo(
    () => diningPayload?.reservations ?? [],
    [diningPayload?.reservations],
  );
  const apiRestaurants: Restaurant[] = useMemo(
    () => diningPayload?.restaurants ?? [],
    [diningPayload?.restaurants],
  );
  const [localReservations, setLocalReservations] = useState<DiningReservation[]>([]);
  const [synced, setSynced] = useState(false);
  if (!synced && apiReservations.length > 0) { setLocalReservations(apiReservations); setSynced(true); }
  const reservations = synced ? localReservations : apiReservations;
  const setReservations = setLocalReservations;
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [cancelTarget, setCancelTarget] = useState<DiningReservation | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [modifyTarget, setModifyTarget] = useState<{ restaurant: Restaurant; reservation: DiningReservation } | null>(null);

  /* ── Stats ── */
  const stats = useMemo(() => {
    const total = reservations.length;
    const upcoming = reservations.filter((r) => r.status === 'Confirmed').length;
    const completed = reservations.filter((r) => r.status === 'Completed').length;
    const cancelled = reservations.filter((r) => r.status === 'Cancelled').length;
    return { total, upcoming, completed, cancelled };
  }, [reservations]);

  /* ── Tab counts ── */
  const tabCounts = useMemo(() => ({
    all: reservations.length,
    Confirmed: stats.upcoming,
    Completed: stats.completed,
    Cancelled: stats.cancelled,
  }), [reservations.length, stats]);

  /* ── Filtered list ── */
  const filtered = useMemo(
    () => activeTab === 'all' ? reservations : reservations.filter((r) => r.status === activeTab),
    [reservations, activeTab],
  );

  /* ── Handlers ── */
  const handleCancel = useCallback(() => {
    if (!cancelTarget) return;
    setReservations((prev) =>
      prev.map((r) => r.id === cancelTarget.id ? { ...r, status: 'Cancelled' as const } : r),
    );
    setCancelSuccess(true);
  }, [cancelTarget, setReservations]);

  const closeCancel = useCallback(() => {
    setCancelTarget(null);
    setCancelSuccess(false);
  }, []);

  const handleModify = useCallback((res: DiningReservation) => {
    const restaurant = apiRestaurants.find((r) => r.id === res.restaurantId);
    if (restaurant) {
      setModifyTarget({ restaurant, reservation: res });
    }
  }, [apiRestaurants]);

  return (
    <div>
      <PageHeader
        backHref="/account"
        title={t('dining.diningReservations')}
        description={t('dining.manageReservations')}
      />

      {/* ── Stats Row ── */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          id="guest-dining-stat-total"
          label={t('dining.stats.total')}
          value={stats.total}
          icon={BarChart3}
          accent="bg-hms-blue/10 text-hms-blue"
        />
        <StatCard
          id="guest-dining-stat-upcoming"
          label={t('dining.upcoming')}
          value={stats.upcoming}
          icon={Calendar}
          accent="bg-hms-gold/10 text-hms-gold"
        />
        <StatCard
          id="guest-dining-stat-completed"
          label={t('dining.stats.completed')}
          value={stats.completed}
          icon={CheckCircle2}
          accent="bg-hms-green/10 text-hms-green"
        />
        <StatCard
          id="guest-dining-stat-cancelled"
          label={t('dining.stats.cancelled')}
          value={stats.cancelled}
          icon={XCircle}
          accent="bg-hms-red/10 text-hms-red"
        />
      </div>

      {/* ── Tab Navigation ── */}
      <div id="guest-dining-tabs" className="mt-6 flex gap-1 border-b border-hms-border">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            id={`guest-dining-tab-${tab.key}`}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'px-4 py-2.5 text-[12px] font-medium transition-colors border-b-2 -mb-px',
              activeTab === tab.key
                ? 'border-hms-gold text-hms-gold'
                : 'border-transparent text-hms-textSub hover:text-hms-text hover:border-hms-border',
            )}
          >
            {tab.labelKey ? t(tab.labelKey) : tab.key}
            <span className={cn(
              'ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-semibold',
              activeTab === tab.key ? 'bg-hms-gold/15 text-hms-gold' : 'bg-hms-surface text-hms-textDim',
            )}>
              {tabCounts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      {/* ── Reservation Cards ── */}
      <div className="mt-5 space-y-4">
        {filtered.length === 0 ? (
          <Card id="guest-dining-empty">
            <div className="py-8 text-center">
              <UtensilsCrossed className="w-8 h-8 text-hms-textDim mx-auto mb-2" />
              <p className="text-[13px] text-hms-textDim">
                {t('dining.noDiningReservations')}
              </p>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {filtered.map((res) => (
              <Card
                key={res.id}
                id={`guest-dining-${res.id}`}
                className={cn(res.status === 'Confirmed' && '!border-hms-gold/20')}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-[14px] font-semibold text-hms-text">{res.restaurantName}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-hms-teal font-mono">{res.id}</span>
                      {res.restaurantType && (
                        <span className="rounded-md bg-hms-surface border border-hms-border/50 px-1.5 py-0.5 text-[9px] font-medium text-hms-textDim">
                          {res.restaurantType}
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge variant={STATUS_VARIANT[res.status]}>{t(RESERVATION_STATUS_KEYS[res.status])}</Badge>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-[12px] text-hms-textSub">
                    <Calendar className="w-3.5 h-3.5 text-hms-textDim" /> {res.date}
                  </div>
                  <div className="flex items-center gap-2 text-[12px] text-hms-textSub">
                    <Clock className="w-3.5 h-3.5 text-hms-textDim" /> {res.time}
                  </div>
                  <div className="flex items-center gap-2 text-[12px] text-hms-textSub">
                    <Users className="w-3.5 h-3.5 text-hms-textDim" /> {res.guests} {res.guests > 1 ? t('dining.guests') : t('dining.guest')}
                  </div>
                  {res.seatingPreference && (
                    <div className="flex items-center gap-2 text-[12px] text-hms-textSub">
                      <Armchair className="w-3.5 h-3.5 text-hms-textDim" /> {res.seatingPreference}
                    </div>
                  )}
                  {res.occasion && (
                    <p className="text-[11px] text-hms-textDim">{t('dining.occasion')}: {res.occasion}</p>
                  )}
                  {res.specialRequests && (
                    <p className="text-[11px] text-hms-textDim italic">{t('common.note')}: {res.specialRequests}</p>
                  )}
                </div>

                {res.status === 'Confirmed' && (
                  <div className="flex gap-2 mt-4">
                    <Button
                      id={`guest-dining-modify-${res.id}`}
                      variant="secondary"
                      size="sm"
                      leftIcon={<PenLine className="w-3.5 h-3.5" />}
                      onClick={() => handleModify(res)}
                    >
                      {t('common.modify')}
                    </Button>
                    <Button
                      id={`guest-dining-cancel-${res.id}`}
                      variant="danger"
                      size="sm"
                      leftIcon={<XCircle className="w-3.5 h-3.5" />}
                      onClick={() => { setCancelTarget(res); setCancelSuccess(false); }}
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ── Cancel Confirmation Modal ── */}
      <Modal
        open={!!cancelTarget}
        onClose={closeCancel}
        title={cancelSuccess ? t('dining.reservationCancelled') : t('dining.cancelReservation')}
        size="sm"
      >
        {cancelSuccess ? (
          <div className="flex flex-col items-center gap-3 py-4">
            <CheckCircle2 className="w-10 h-10 text-hms-green" />
            <p id="guest-dining-cancel-success" className="text-[14px] text-hms-text font-medium text-center">
              {t('dining.reservationHasBeenCancelled', { id: cancelTarget?.id ?? 'N/A' })}
            </p>
            <Button id="guest-dining-cancel-done" variant="secondary" onClick={closeCancel}>{t('common.done')}</Button>
          </div>
        ) : cancelTarget && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-hms-red/5 border border-hms-red/20">
              <AlertTriangle className="w-5 h-5 text-hms-red shrink-0 mt-0.5" />
              <p className="text-[12px] text-hms-textSub">
                {t('dining.confirmCancelReservation', { name: cancelTarget.restaurantName, date: cancelTarget.date, time: cancelTarget.time })}
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button id="guest-dining-cancel-keep" variant="secondary" onClick={closeCancel}>{t('dining.keepReservation')}</Button>
              <Button id="guest-dining-cancel-confirm" variant="danger" onClick={handleCancel}>{t('dining.cancelReservation')}</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Modify Drawer ── */}
      {modifyTarget && (
        <DiningReservationDrawer
          restaurant={modifyTarget.restaurant}
          onClose={() => setModifyTarget(null)}
          editMode={{
            date: modifyTarget.reservation.date,
            time: modifyTarget.reservation.time,
            guests: modifyTarget.reservation.guests,
            occasion: modifyTarget.reservation.occasion || '',
            seatingPreference: modifyTarget.reservation.seatingPreference || 'Indoor',
            specialRequests: modifyTarget.reservation.specialRequests || '',
          }}
        />
      )}
    </div>
  );
}
